import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import useEventCallback from '@ecp/common/src/hooks/useEventCallback';
import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox } from '@ecp/common/src/layouts/flex/styled';
import HighlightText from '@ecp/common/src/text/HighlightText';

const ResultItemWrapper = styled(FlexBox).attrs(
  ({ height = '30px', color = boTheme.color.sub1, hovered, transform = 'none', style = {} }) => ({
    style: {
      ...style,
      height,
      lineHeight: height,
      color: color,
      background: hovered ? boTheme.color.table4 : boTheme.color.sub10,
      transform,
    },
  })
)`
  width: 100%;
  font-weight: ${boTheme.font.weight.regular};
  font-size: ${boTheme.font.size.small};
  cursor: pointer;
  box-sizing: border-box;
  padding-left: 10px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: absolute;
  top: 0;
  left: 0;
`;

const NoDataItem = styled(ResultItemWrapper)`
  cursor: not-allowed;
  color: ${boTheme.color.sub4};
  height: 29px;
  line-height: 29px;
`;

const ResultItemList = ({
  items,
  keyword,
  handleItemClick,
  styleHighlight,
  styleNormal,
  virtualizer,
  handleEnter: handleEnterProp,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  const handleKeydown = useCallback(
    (e) => {
      const { key, isComposing } = e;
      if (!isComposing && key === 'ArrowUp') {
        setHoveredIndex((prev) => {
          const newHoveredIndex = Math.max(0, prev - 1);
          virtualizer?.scrollToIndex?.(newHoveredIndex, { align: 'auto', smoothScroll: true });
          return newHoveredIndex;
        });
      } else if (!isComposing && key === 'ArrowDown') {
        setHoveredIndex((prev) => {
          const newHoveredIndex = Math.min(items.length - 1, prev + 1);
          virtualizer?.scrollToIndex?.(newHoveredIndex, { align: 'auto', smoothScroll: true });
          return newHoveredIndex;
        });
      }
    },
    [virtualizer, items.length]
  );

  const handleEnter = useCallback(
    (e) => {
      const { key, isComposing } = e;
      if (!isComposing && key === 'Enter') {
        e.stopPropagation();
        const selectedItem = items?.[hoveredIndex];
        virtualizer && selectedItem ? handleItemClick?.(selectedItem.itemLabel, selectedItem) : handleEnterProp?.(e);
      }
    },
    [handleEnterProp, handleItemClick, hoveredIndex, items, virtualizer]
  );

  useEffect(() => {
    const remove = () => {
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('keyup', handleEnter, true);
    };

    (() => {
      window.addEventListener('keydown', handleKeydown);
      window.addEventListener('keyup', handleEnter, true);
    })();
    return () => remove();
  }, [handleEnter, handleKeydown]);

  const onItemClick = useEventCallback(
    (value, data) => {
      const selectedItem = items[data?.index];
      selectedItem && handleItemClick?.(selectedItem.itemLabel, selectedItem);
    },
    { stop: true }
  );

  return (
    <div
      style={{
        height: `${virtualizer?.getTotalSize()}px`,
        width: '100%',
        position: 'relative',
      }}
    >
      {virtualizer?.getTotalSize() ? (
        virtualizer.getVirtualItems().map(({ index, size, start }) => (
          <ResultItemWrapper
            key={`autocomplete__${index}`}
            data-index={index}
            onClick={onItemClick}
            onMouseDown={(e) => e.preventDefault()}
            onMouseOver={() => setHoveredIndex(index)}
            hovered={index === hoveredIndex}
            height={`${size}px`}
            transform={`translateY(${start}px)`}
            style={{ ...styleNormal }}
          >
            <HighlightText keyword={keyword} style={styleHighlight}>
              {items?.[index]?.itemLabel}
            </HighlightText>
          </ResultItemWrapper>
        ))
      ) : (
        <NoDataItem>검색결과가 없습니다.</NoDataItem>
      )}
    </div>
  );
};

ResultItemList.displayName = 'ResultItemList';

ResultItemList.propTypes = {
  items: PropTypes.array,
  keyword: PropTypes.string,
  handleItemClick: PropTypes.func,
  styleHighlight: PropTypes.object,
  styleNormal: PropTypes.object,
  virtualizer: PropTypes.object,
  handleEnter: PropTypes.func,
};

export default ResultItemList;
